import type { JSX } from 'react';
import { styled } from 'styled-components';
import { Item as ToggleGroupItem } from '@radix-ui/react-toggle-group';
import { formatHex } from 'culori/fn';
import { ColorSwatch } from './ColorSwatch';
import { ColorNameOrHexCode } from './ColorNameOrHexCode';
import { useColorPickerState } from './ColorPickerContext';
import type { ColorPickerOption } from './types';

const Container = styled(ToggleGroupItem)`
  border: none;
  appearance: none;
  outline: none;
  align-items: center;
  background-color: var(--wui-color-bg-surface);
  border-radius: var(--wui-border-radius-01);
  cursor: pointer;
  display: flex;
  gap: var(--wui-space-02);
  padding: 6px;
  user-select: none;

  &:hover,
  &:focus-visible {
    background-color: var(--wui-color-bg-surface-hover);
  }

  &:focus-visible {
    background-color: var(--wui-color-bg-surface-secondary-hover);
  }
`;

export type ColorListOptionProps = ColorPickerOption;

/**
 * A single selectable row for use inside a `ColorList`. Displays a color
 * swatch alongside the color's name (if provided) or hex code.
 */
export const ColorListOption = ({ value: rawValue, name }: ColorListOptionProps): JSX.Element => {
  const value = rawValue.toLocaleLowerCase();
  const { valueAsHsv, nonDerivedValueAsHex } = useColorPickerState();
  // It's important that we use the internal valueAsHsv here rather than using
  // valueAsHex (which is the value passed into the ColorPicker as a prop), to
  // avoid a small timing issue: If we used valueAsHex from useColorPickerState,
  // derivativeIsSelected would be momentarily true here when it shouldn't be,
  // i.e. before the outer component renders to update the prop value after the
  // valueAsHsv has updated internally.
  const valueAsHex = formatHex(valueAsHsv);
  const isSelected = valueAsHex === value;
  const derivativeIsSelected = !isSelected && nonDerivedValueAsHex === value;

  return (
    <Container
      aria-label={value}
      aria-selected={valueAsHex === value}
      role="option"
      tabIndex={-1}
      value={value}
    >
      <ColorSwatch
        derivativeIsSelected={derivativeIsSelected}
        isSelected={isSelected}
        value={value}
      />
      <ColorNameOrHexCode
        hexCode={value}
        name={name}
      />
    </Container>
  );
};

ColorListOption.displayName = 'ColorListOption';
